import "./style.css";
import type { DrawMode, GameState, Move } from "./game/types.ts";
import { generateSolvableGame } from "./game/generate.ts";
import { dealKlondike } from "./game/deal.ts";
import {
  canAutocomplete,
  undo as undoMove,
  applyMove,
  drawFromStock,
  findFoundationPushFromTops,
  isStuck,
} from "./game/moves.ts";
import { computeWinXp } from "./game/scoring.ts";
import {
  canDrawJoker,
  drawJoker,
  ensureJokerState,
  availableJokerCards,
  finalizePendingJokerSpend,
  grantJokerReward,
  jokerWasRewound,
  levelUpJokerReward,
  removeJokersForAutocomplete,
} from "./game/jokers.ts";
import { computeLayout } from "./ui/layout.ts";
import { createRenderTargets, render } from "./ui/render.ts";
import { attachInput } from "./ui/input.ts";
import { injectSuitSprite } from "./ui/suits.ts";
import {
  loadProgress,
  loadSettings,
  loadStats,
  recordGameLoss,
  recordGameResult,
  resetDetailedStats,
  saveProgress,
  saveSettings,
  saveStats,
} from "./storage/local.ts";
import { levelFromCumulativeXp } from "./config/constants.ts";
import {
  playDraw,
  playFoundation,
  playInvalid,
  playPlace,
  playUndo,
  playWin,
  setMuted,
  unlockOnFirstGesture,
} from "./audio/sounds.ts";
import { ensureAuthenticated } from "./sync/supabase.ts";
import {
  initStatsSync,
  scheduleStatsUpload,
} from "./sync/stats-sync.ts";
import {
  ensureGeneratedDisplayName,
  fetchLeaderboard,
} from "./sync/leaderboard.ts";
import {
  fetchWorldRecord,
  rememberClearedSeed,
  recordDealClear,
  type WorldRecord,
} from "./sync/deals-pool.ts";
import {
  scheduleCurrentGameSync,
  syncCurrentGameNow,
} from "./sync/current-game.ts";
import {
  initJokerSync,
  recordJokerLedger,
} from "./sync/jokers-sync.ts";

const boardEl = document.getElementById("board") as HTMLElement;
const overlayEl = document.getElementById("overlay") as HTMLElement;
const btnNew = document.getElementById("btn-new") as HTMLButtonElement;
const btnUndo = document.getElementById("btn-undo") as HTMLButtonElement;
const btnHint = document.getElementById("btn-hint") as HTMLButtonElement;
const btnSettings = document.getElementById("btn-settings") as HTMLButtonElement;
const btnRanking = document.getElementById("btn-ranking") as HTMLButtonElement;
const btnJoker = document.getElementById("btn-joker") as HTMLButtonElement;
const jokerCountEl = document.getElementById("joker-count") as HTMLElement;
const statusTime = document.getElementById("status-time") as HTMLElement;
const statusMoves = document.getElementById("status-moves") as HTMLElement;
const statusScore = document.getElementById("status-score") as HTMLElement;
const toastEl = document.getElementById("toast") as HTMLElement;

let settings = loadSettings();
let stats = loadStats();

setMuted(!settings.soundEnabled);
unlockOnFirstGesture();

const stateRef: { current: GameState | null } = { current: null };
const targets = createRenderTargets(boardEl);
let layout = computeLayout(
  boardEl.clientWidth,
  boardEl.clientHeight,
  settings.foundationOnLeft,
);
// Timer starts only after the first move. Null means "not started yet" — show 00:00.
let timerStartedAt: number | null = null;
let timerHandle: number | null = null;

let lastFoundationTotal = 0;
const UNDO_ELEVATION_MS = 220;
const UNDO_HOLD_DELAY_MS = 360;
const UNDO_REPEAT_MS = 105;
let autocompleteBlockedAtMoves: number | null = null;
let dismissedStuckAtMoves: number | null = null;

function showPanelOverlay(): void {
  overlayEl.classList.remove("settings-mode");
  overlayEl.classList.remove("hidden");
}

function showSettingsOverlay(): void {
  overlayEl.classList.add("settings-mode");
  overlayEl.classList.remove("hidden");
}

function hideOverlay(): void {
  overlayEl.classList.add("hidden");
  overlayEl.classList.remove("settings-mode");
}

function rerender(): void {
  if (!stateRef.current) return;
  ensureJokerState(stateRef.current);
  layout = computeLayout(
    boardEl.clientWidth,
    boardEl.clientHeight,
    settings.foundationOnLeft,
  );
  render(stateRef.current, layout, targets, new Set());
  updateJokerButton();

  // Start the clock the first time the player makes a move.
  if (timerStartedAt == null && stateRef.current.moves > 0) {
    timerStartedAt = Date.now();
  }

  // Foundation sound on growth
  const total = foundationCount(stateRef.current);
  if (total > lastFoundationTotal) {
    if (settings.soundEnabled) playFoundation();
  }
  lastFoundationTotal = total;

  if (isStuck(stateRef.current)) {
    autocompleteBlockedAtMoves = stateRef.current.moves;
    saveProgress(stateRef.current);
    scheduleCurrentGameSync(stateRef.current, "stuck");
    updateStatusBar();
    if (dismissedStuckAtMoves !== stateRef.current.moves) {
      showStuckOverlay();
    }
    return;
  }

  if (
    !stateRef.current.finished &&
    canAutocomplete(stateRef.current) &&
    autocompleteBlockedAtMoves !== stateRef.current.moves &&
    foundationCount(stateRef.current) < 52
  ) {
    runAutocomplete();
    return;
  }

  saveProgress(stateRef.current);
  scheduleCurrentGameSync(stateRef.current);
  updateStatusBar();

  if (stateRef.current.finished && stateRef.current.won) {
    handleWin();
    return;
  }
}

function updateJokerButton(): void {
  jokerCountEl.textContent = String(availableJokerCards(stats, stateRef.current));
  btnJoker.classList.toggle(
    "hidden",
    !canDrawJoker(stateRef.current, stats),
  );
}

function foundationCount(s: GameState): number {
  return s.foundation.reduce(
    (acc, p) => acc + p.filter((card) => !card.isJoker).length,
    0,
  );
}

function runAutocomplete(): void {
  const s = stateRef.current;
  if (!s) return;
  removeJokersForAutocomplete(s);

  // Hard cap on consecutive draws-without-push to detect "no progress possible"
  // and fall back to a manual play state instead of looping forever.
  let drawsSinceLastPush = 0;
  const maxIdleDraws = (s.stock.length + s.waste.length) * 2 + 4;

  const tick = () => {
    if (!stateRef.current || s.finished) return;

    const push = findFoundationPushFromTops(s);
    if (push) {
      applyMove(s, push);
      drawsSinceLastPush = 0;
      rerender();
      if (!s.finished) window.setTimeout(tick, 70);
      return;
    }

    // No immediate push — try to draw / recycle to find a usable card.
    if (s.stock.length === 0 && s.waste.length === 0) {
      // Truly nothing left to do. Stop autocomplete; rerender will show win or stuck.
      rerender();
      return;
    }
    if (drawsSinceLastPush >= maxIdleDraws) {
      // Cycled through everything without finding a push. Stop here.
      autocompleteBlockedAtMoves = s.moves;
      rerender();
      return;
    }
    drawFromStock(s);
    drawsSinceLastPush++;
    rerender();
    window.setTimeout(tick, 50);
  };
  window.setTimeout(tick, 80);
}

function updateStatusBar(): void {
  const s = stateRef.current;
  if (!s) return;
  const liveMs = timerStartedAt != null ? Date.now() - timerStartedAt : 0;
  const elapsedSec = Math.floor((liveMs + s.elapsedMs) / 1000);
  const mm = String(Math.floor(elapsedSec / 60)).padStart(2, "0");
  const ss = String(elapsedSec % 60).padStart(2, "0");
  statusTime.textContent = `${mm}:${ss}`;
  statusMoves.textContent = String(s.moves);

  statusScore.textContent = String(s.score ?? 0);
}

function startTimer(): void {
  if (timerHandle != null) window.clearInterval(timerHandle);
  // Display tick only — actual elapsed time begins on first move.
  timerHandle = window.setInterval(() => {
    if (stateRef.current && !stateRef.current.finished) updateStatusBar();
  }, 500);
}

function resetTimerForNewGame(): void {
  timerStartedAt = null;
}

function handleWin(): void {
  const s = stateRef.current!;
  const liveMs = timerStartedAt != null ? Date.now() - timerStartedAt : 0;
  const elapsedSec = Math.floor((liveMs + s.elapsedMs) / 1000);
  s.elapsedMs = elapsedSec * 1000;
  timerStartedAt = null;

  const result = recordGameResult(stats, s, elapsedSec);
  const xp = computeWinXp(s, {
    isFirstDailyWin: result.isFirstDailyWin,
    currentStreakDays: result.stats.currentStreakDays,
  });
  const previousXp = result.stats.cumulativeXp;
  result.stats.cumulativeXp += xp.total;
  const jokerReward = levelUpJokerReward(previousXp, result.stats.cumulativeXp);
  result.stats = grantJokerReward(result.stats, jokerReward);
  const jokerSpend = finalizePendingJokerSpend(result.stats, s);
  result.stats = jokerSpend.stats;
  stats = result.stats;
  saveStats(stats);
  scheduleStatsUpload(stats);
  void syncWinJokerLedger(s, jokerReward, jokerSpend.spent);
  void syncCurrentGameNow(s, "won");
  rememberClearedSeed(s.drawMode, s.seed);
  saveProgress(null);

  // Record per-deal clear & fetch the (pre-our-record) world record so we can show it.
  void recordDealClear(s.seed, elapsedSec, s.moves);
  void fetchWorldRecord(s.seed).then((wr) => {
    showWinOverlay(
      elapsedSec,
      xp.total,
      result.isFirstDailyWin,
      s.seed,
      s.moves,
      wr,
      jokerReward?.count ?? 0,
    );
  });

  if (settings.soundEnabled) playWin();
  // Show the overlay immediately with no WR data; the async fetch above will replace it.
  showWinOverlay(
    elapsedSec,
    xp.total,
    result.isFirstDailyWin,
    s.seed,
    s.moves,
    null,
    jokerReward?.count ?? 0,
  );
}

async function syncWinJokerLedger(
  s: GameState,
  jokerReward: { count: number; levelsGained: number } | null,
  jokerSpent: boolean,
): Promise<void> {
  if (jokerReward && jokerReward.count > 0) {
    await recordJokerLedger({
      delta: jokerReward.count,
      reason: "level_up",
      gameSeed: s.seed,
      idempotencyKey: `level-up:${s.seed}:${s.startedAt}`,
      note: `${jokerReward.levelsGained} level(s) gained`,
    });
  }
  if (jokerSpent) {
    await recordJokerLedger({
      delta: -1,
      reason: "game_spend",
      gameSeed: s.seed,
      idempotencyKey: `joker-spend:${s.seed}:${s.startedAt}`,
      note: "Finalized at win",
    });
  }
}

function showWinOverlay(
  elapsedSec: number,
  xpGained: number,
  isFirstDaily: boolean,
  _seed: string,
  movesUsed: number,
  wr: WorldRecord | null,
  jokersGained: number,
): void {
  const mm = Math.floor(elapsedSec / 60);
  const ss = elapsedSec % 60;
  const lvl = levelFromCumulativeXp(stats.cumulativeXp);

  let wrLine = "";
  if (wr && (wr.wrTime != null || wr.wrMoves != null)) {
    const wrMM = wr.wrTime != null ? Math.floor(wr.wrTime / 60) : null;
    const wrSS = wr.wrTime != null ? wr.wrTime % 60 : null;
    const wrTimeStr =
      wrMM != null ? `${wrMM}:${String(wrSS).padStart(2, "0")}` : "—";
    const wrMovesStr = wr.wrMoves != null ? String(wr.wrMoves) : "—";
    const isYourTimeRecord = wr.wrTime != null && elapsedSec <= wr.wrTime;
    const isYourMovesRecord = wr.wrMoves != null && movesUsed <= wr.wrMoves;
    const newRec = (isYourTimeRecord || isYourMovesRecord) ? "  ⭐ NEW" : "";
    wrLine = `
      <p style="font-size:0.8rem; opacity:0.85; margin-top:8px;">
        🏆 World: ${wrTimeStr} / ${wrMovesStr} moves<br/>
        <span style="opacity:0.6;">(${wr.playersCleared} cleared)${newRec}</span>
      </p>
    `;
  }

  showPanelOverlay();
  overlayEl.innerHTML = `
    <div class="panel">
      <h2>Cleared!</h2>
      <p>${mm}:${String(ss).padStart(2, "0")} / ${movesUsed} moves</p>
      <p>+${xpGained} XP${isFirstDaily ? " (×2 daily bonus)" : ""}</p>
      ${jokersGained > 0 ? `<p>Joker +${jokersGained}</p>` : ""}
      <p>Lv${lvl.level}  ${lvl.xpInLevel}/${
        Number.isFinite(lvl.xpForNext) ? lvl.xpForNext : "MAX"
      }</p>
      ${wrLine}
      <button class="btn" id="btn-overlay-new" style="margin-top:8px;">New Game</button>
    </div>
  `;
  document.getElementById("btn-overlay-new")?.addEventListener("click", () => {
    hideOverlay();
    void newGame();
  });
}

function showStuckOverlay(): void {
  const s = stateRef.current;
  const canUseJoker = canDrawJoker(s, stats);
  const usedJoker = s?.joker?.usedThisGame === true;
  showPanelOverlay();
  overlayEl.innerHTML = `
    <div class="panel stuck-panel" role="dialog" aria-modal="true" aria-label="No moves left">
      <button class="overlay-close" id="btn-stuck-close" type="button" aria-label="Close">×</button>
      <h2>これ以上は移動できません</h2>
      <p>${usedJoker ? "ジョーカーを使っても進めない状態です。" : "ジョーカーを使うと続行できる可能性があります。"}</p>
      <div class="stuck-joker-card" aria-hidden="true">J</div>
      <div class="stuck-actions">
        ${
          canUseJoker
            ? `<button class="btn primary" id="btn-stuck-joker" type="button">ジョーカーを使う</button>`
            : `<button class="btn primary" id="btn-stuck-restart" type="button">もう一度最初から</button>`
        }
        <button class="btn secondary" id="btn-stuck-new" type="button">新しいゲーム</button>
      </div>
    </div>
  `;
  document.getElementById("btn-stuck-close")?.addEventListener("click", () => {
    dismissedStuckAtMoves = stateRef.current?.moves ?? null;
    hideOverlay();
    updateJokerButton();
  });
  document.getElementById("btn-stuck-joker")?.addEventListener("click", () => {
    handleDrawJoker();
  });
  document.getElementById("btn-stuck-restart")?.addEventListener("click", () => {
    hideOverlay();
    restartCurrentGame();
  });
  document.getElementById("btn-stuck-new")?.addEventListener("click", () => {
    recordCurrentGameLoss();
    hideOverlay();
    void newGame();
  });
}

function handleDrawJoker(): boolean {
  const s = stateRef.current;
  if (!canDrawJoker(s, stats) || !s) return false;
  if (!drawJoker(s)) return false;
  saveStats(stats);
  scheduleStatsUpload(stats);
  dismissedStuckAtMoves = null;
  hideOverlay();
  if (settings.soundEnabled) playDraw();
  rerender();
  return true;
}

function finalizeCurrentPendingJokerSpend(): boolean {
  const s = stateRef.current;
  const result = finalizePendingJokerSpend(stats, stateRef.current);
  if (!result.spent) return false;
  stats = result.stats;
  saveStats(stats);
  scheduleStatsUpload(stats);
  if (s) {
    void recordJokerLedger({
      delta: -1,
      reason: "game_spend",
      gameSeed: s.seed,
      idempotencyKey: `joker-spend:${s.seed}:${s.startedAt}`,
      note: "Finalized before leaving game",
    });
  }
  updateJokerButton();
  return true;
}

function revealDismissedStuckOverlay(): void {
  const s = stateRef.current;
  if (!s) return;
  if (isStuck(s) && dismissedStuckAtMoves === s.moves) {
    dismissedStuckAtMoves = null;
    showStuckOverlay();
  }
}

function recordCurrentGameLoss(): void {
  const s = stateRef.current;
  if (!s || s.finished) return;
  stats = recordGameLoss(stats, s);
  saveStats(stats);
  scheduleStatsUpload(stats);
}

function showLoading(message: string): void {
  showPanelOverlay();
  overlayEl.innerHTML = `
    <div class="panel">
      <h2>Just Solitaire</h2>
      <p id="loading-msg">${message}</p>
    </div>
  `;
}

function setLoadingMessage(message: string): void {
  const el = document.getElementById("loading-msg");
  if (el) el.textContent = message;
}

function showRetryNewGame(message: string): void {
  showPanelOverlay();
  overlayEl.innerHTML = `
    <div class="panel">
      <h2>Just Solitaire</h2>
      <p>${message}</p>
      <button class="btn" id="btn-retry-new">Retry</button>
    </div>
  `;
  const retry = () => void newGame();
  document.getElementById("btn-retry-new")?.addEventListener("click", (ev) => {
    ev.stopPropagation();
    retry();
  });
  overlayEl.addEventListener("click", (ev) => {
    if (ev.target === overlayEl) retry();
  }, { once: true });
}

async function newGame(): Promise<void> {
  finalizeCurrentPendingJokerSpend();
  showLoading("Generating...");
  try {
    const game = await generateSolvableGame(settings.drawMode, {
      onProgress: ({ attempt, total }) => {
        setLoadingMessage(
          total == null
            ? `Generating... (attempt ${attempt})`
            : `Generating... (${attempt}/${total})`,
        );
      },
    });
    game.scoringSystem = settings.scoringSystem;
    stateRef.current = game;
    ensureJokerState(game);
    lastFoundationTotal = foundationCount(game);
    dismissedStuckAtMoves = null;
    autocompleteBlockedAtMoves = null;
    saveProgress(game);
    void syncCurrentGameNow(game, "active");
    hideOverlay();
    resetTimerForNewGame();
    startTimer();
    rerender();
  } catch (err) {
    console.error(err);
    showRetryNewGame("Failed to generate a verified deal.");
  }
}

function restartCurrentGame(): void {
  const current = stateRef.current;
  if (!current) {
    void newGame();
    return;
  }
  finalizeCurrentPendingJokerSpend();
  const game = dealKlondike(current.seed, current.drawMode);
  game.scoringSystem = settings.scoringSystem;
  stateRef.current = game;
  ensureJokerState(game);
  lastFoundationTotal = foundationCount(game);
  autocompleteBlockedAtMoves = null;
  dismissedStuckAtMoves = null;
  saveProgress(game);
  void syncCurrentGameNow(game, "active");
  resetTimerForNewGame();
  startTimer();
  rerender();
}

function handleUndo(): boolean {
  const s = stateRef.current;
  if (!s || s.undoStack.length === 0) return false;
  ensureJokerState(s);
  const beforeJokerState: GameState = {
    ...s,
    joker: { ...s.joker },
  };
  const before = cardLocations(s);
  if (undoMove(s)) {
    ensureJokerState(s);
    if (jokerWasRewound(beforeJokerState, s)) {
      updateJokerButton();
    }
    dismissedStuckAtMoves = null;
    const movedCardIds = changedCardIds(before, cardLocations(s));
    if (settings.soundEnabled) playUndo();
    rerender();
    elevateCardsDuringUndo(movedCardIds);
    return true;
  }
  return false;
}

interface CardLocation {
  pile: string;
  index: number;
  faceUp: boolean;
}

function cardLocations(s: GameState): Map<number, CardLocation> {
  const map = new Map<number, CardLocation>();
  s.tableau.forEach((pile, pileIndex) => {
    pile.forEach((card, index) => {
      map.set(card.id, { pile: `tableau:${pileIndex}`, index, faceUp: card.faceUp });
    });
  });
  s.foundation.forEach((pile, pileIndex) => {
    pile.forEach((card, index) => {
      map.set(card.id, { pile: `foundation:${pileIndex}`, index, faceUp: card.faceUp });
    });
  });
  s.stock.forEach((card, index) => {
    map.set(card.id, { pile: "stock:0", index, faceUp: card.faceUp });
  });
  s.waste.forEach((card, index) => {
    map.set(card.id, { pile: "waste:0", index, faceUp: card.faceUp });
  });
  return map;
}

function changedCardIds(
  before: Map<number, CardLocation>,
  after: Map<number, CardLocation>,
): number[] {
  const changed: number[] = [];
  for (const [id, next] of after) {
    const prev = before.get(id);
    if (!prev) continue;
    if (
      prev.pile !== next.pile ||
      prev.index !== next.index ||
      prev.faceUp !== next.faceUp
    ) {
      changed.push(id);
    }
  }
  return changed;
}

function elevateCardsDuringUndo(cardIds: number[]): void {
  if (cardIds.length === 0) return;
  cardIds.forEach((id, idx) => {
    const el = boardEl.querySelector<HTMLElement>(`.card[data-card-id="${id}"]`);
    if (el) el.style.zIndex = String(9999 + idx);
  });
  window.setTimeout(() => rerender(), UNDO_ELEVATION_MS);
}

function handleHint(): void {
  if (!stateRef.current) return;
  const s = stateRef.current;
  const move = findHintMove(s);
  if (move) {
    s.hintsUsed += 1;
    animateHintMove(move);
    return;
  }
  if (isStuck(s)) {
    saveProgress(s);
    scheduleCurrentGameSync(s, "stuck");
    showStuckOverlay();
    return;
  }
  // No useful hint yet, but cycling stock/waste can still reveal one.
  if (s.stock.length > 0 || s.waste.length > 0) {
    flashStatus(s.stock.length > 0 ? "Draw from the stock." : "Recycle the waste.");
    flashStock();
    return;
  }
  showStuckOverlay();
}

// Animate the source card(s) sliding to its destination, hold, then back.
// For tableau→tableau hints with a multi-card run, the whole run animates as a unit.
function animateHintMove(move: HintMove): void {
  const s = stateRef.current;
  if (!s) return;
  const cardIds = collectHintSlice(s, move);
  if (cardIds.length === 0) return;

  const destHead = computeDestPosition(move);
  if (!destHead) {
    flashCard(move.cardId);
    return;
  }

  // Head goes at destHead. Subsequent cards stack with stackGapUp on tableau.
  const stepY = move.toKind === "tableau" ? layout.stackGapUp : 0;
  const elements: Array<{
    el: HTMLElement;
    transform: string;
    zIndex: string;
    transition: string;
  }> = [];

  cardIds.forEach((id, idx) => {
    const el = boardEl.querySelector<HTMLElement>(`.card[data-card-id="${id}"]`);
    if (!el) return;
    elements.push({
      el,
      transform: el.style.transform,
      zIndex: el.style.zIndex,
      transition: el.style.transition,
    });
    el.classList.add("hint-moving");
    el.style.zIndex = String(9999 + idx);
    el.style.transition = "transform 0.35s ease";
    el.style.transform = `translate(${destHead.x}px, ${destHead.y + idx * stepY}px)`;
  });

  window.setTimeout(() => {
    elements.forEach(({ el, transform }) => {
      el.style.transform = transform;
    });
    window.setTimeout(() => {
      elements.forEach(({ el, zIndex, transition }) => {
        el.classList.remove("hint-moving");
        el.style.transition = transition;
        el.style.zIndex = zIndex;
      });
    }, 380);
  }, 700);
}

// Returns the card ids that would be moved as a unit for this hint move:
// for tableau sources, the head card and everything stacked above it;
// for waste sources, just the single top card.
function collectHintSlice(s: GameState, move: HintMove): number[] {
  if (move.fromKind === "tableau") {
    for (const col of s.tableau) {
      const idx = col.findIndex((c) => c.id === move.cardId);
      if (idx >= 0) return col.slice(idx).map((c) => c.id);
    }
    return [];
  }
  // waste
  return [move.cardId];
}

function computeDestPosition(move: HintMove): { x: number; y: number } | null {
  if (!stateRef.current) return null;
  const s = stateRef.current;
  if (move.toKind === "foundation" && move.toIndex != null) {
    const slot = layout.pilePositions.foundation[move.toIndex];
    if (!slot) return null;
    const stackedCount = s.foundation[move.toIndex]?.length ?? 0;
    return { x: slot.x, y: slot.y + stackedCount * 2 };
  }
  if (move.toKind === "tableau" && move.toIndex != null) {
    const slot = layout.pilePositions.tableau[move.toIndex];
    if (!slot) return null;
    const col = s.tableau[move.toIndex] ?? [];
    let y = slot.y;
    for (const c of col) {
      y += c.faceUp ? layout.stackGapUp : layout.stackGapDown;
    }
    return { x: slot.x, y };
  }
  return null;
}

let statusFlashTimer: number | null = null;
function flashStatus(text: string): void {
  toastEl.textContent = text;
  toastEl.classList.remove("hidden");
  if (statusFlashTimer != null) window.clearTimeout(statusFlashTimer);
  statusFlashTimer = window.setTimeout(() => {
    toastEl.classList.add("hidden");
    statusFlashTimer = null;
  }, 1800);
}

interface HintMove {
  cardId: number;
  fromKind: "tableau" | "waste";
  toKind: "tableau" | "foundation";
  toIndex: number;
}

function findHintMove(s: GameState): HintMove | null {
  const findFoundationIndexFor = (
    suit: "S" | "H" | "D" | "C",
    rank: number,
  ): number | null => {
    for (let i = 0; i < s.foundation.length; i++) {
      const top = s.foundation[i]?.[s.foundation[i]!.length - 1];
      if (top && top.suit === suit && top.rank === rank - 1) return i;
    }
    if (rank === 1) {
      for (let i = 0; i < s.foundation.length; i++) {
        if ((s.foundation[i]?.length ?? 0) === 0) return i;
      }
    }
    return null;
  };
  const isAltColor = (a: { suit: string }, b: { suit: string }): boolean =>
    (a.suit === "S" || a.suit === "C") !== (b.suit === "S" || b.suit === "C");

  const w = s.waste[s.waste.length - 1];

  // 1. Foundation push from a top card. This matches smart-tap priority.
  for (let i = 0; i < s.tableau.length; i++) {
    const col = s.tableau[i]!;
    const top = col[col.length - 1];
    if (!top || top.isJoker) continue;
    const fi = findFoundationIndexFor(top.suit, top.rank);
    if (fi != null) {
      return { cardId: top.id, fromKind: "tableau", toKind: "foundation", toIndex: fi };
    }
  }
  if (w && !w.isJoker) {
    const fi = findFoundationIndexFor(w.suit, w.rank);
    if (fi != null) {
      return { cardId: w.id, fromKind: "waste", toKind: "foundation", toIndex: fi };
    }
  }

  // 2. Tableau→tableau move that reveals a face-down card.
  for (let i = 0; i < s.tableau.length; i++) {
    const col = s.tableau[i]!;
    let firstFaceUp = -1;
    for (let k = 0; k < col.length; k++) {
      if (col[k]!.faceUp) {
        firstFaceUp = k;
        break;
      }
    }
    if (firstFaceUp <= 0) continue;
    const head = col[firstFaceUp]!;
    if (head.isJoker) continue;
    for (let j = 0; j < s.tableau.length; j++) {
      if (j === i) continue;
      const dest = s.tableau[j]!;
      const top = dest[dest.length - 1];
      if (!top) {
        if (head.rank === 13) {
          return { cardId: head.id, fromKind: "tableau", toKind: "tableau", toIndex: j };
        }
      } else if (top.faceUp && top.rank === head.rank + 1 && isAltColor(top, head)) {
        return { cardId: head.id, fromKind: "tableau", toKind: "tableau", toIndex: j };
      }
    }
  }

  // 3. Waste → tableau.
  if (w && !w.isJoker) {
    for (let j = 0; j < s.tableau.length; j++) {
      const dest = s.tableau[j]!;
      const top = dest[dest.length - 1];
      if (!top) {
        if (w.rank === 13) {
          return { cardId: w.id, fromKind: "waste", toKind: "tableau", toIndex: j };
        }
      } else if (top.faceUp && top.rank === w.rank + 1 && isAltColor(top, w)) {
        return { cardId: w.id, fromKind: "waste", toKind: "tableau", toIndex: j };
      }
    }
  }

  // 4. Non-revealing tableau moves that are still reasonable hints:
  // whole-column moves and top-card moves. Avoid peeling a visible run out
  // from under another visible card, such as Q-from-K shuffles.
  const exposesFoundationPush = (
    col: typeof s.tableau[number],
    index: number,
  ): boolean => {
    if (index !== col.length - 1 || index <= 0) return false;
    const exposed = col[index - 1];
    if (!exposed || !exposed.faceUp || exposed.isJoker) return false;
    return findFoundationIndexFor(exposed.suit, exposed.rank) != null;
  };

  const findQuietTableauMove = (
    accepts: (col: typeof s.tableau[number], index: number) => boolean,
  ): HintMove | null => {
    for (let i = 0; i < s.tableau.length; i++) {
      const col = s.tableau[i]!;
      for (let k = 0; k < col.length; k++) {
        if (!accepts(col, k)) continue;
        const head = col[k]!;
        if (!head.faceUp || head.isJoker) continue;
        for (let j = 0; j < s.tableau.length; j++) {
          if (j === i) continue;
          const dest = s.tableau[j]!;
          const top = dest[dest.length - 1];
          if (!top) continue;
          if (top.faceUp && top.rank === head.rank + 1 && isAltColor(top, head)) {
            return { cardId: head.id, fromKind: "tableau", toKind: "tableau", toIndex: j };
          }
        }
      }
    }
    return null;
  };

  return (
    findQuietTableauMove((_col, index) => index === 0) ??
    findQuietTableauMove((col, index) =>
      index === col.length - 1 && exposesFoundationPush(col, index)
    )
  );
}

function flashCard(cardId: number): void {
  const el = boardEl.querySelector<HTMLElement>(`.card[data-card-id="${cardId}"]`);
  if (!el) return;
  el.classList.add("hint-flash");
  window.setTimeout(() => el.classList.remove("hint-flash"), 900);
}

// Highlight the stock pile (or its recycle placeholder) so the user knows
// to tap there. Used when the hint engine has nothing better to suggest.
function flashStock(): void {
  const stockCards = Array.from(
    boardEl.querySelectorAll<HTMLElement>(`.card[data-pile="stock:0"]`),
  ).filter((el) => !el.classList.contains("placeholder"));
  let target: HTMLElement | null = null;
  if (stockCards.length > 0) {
    target = stockCards.reduce((best, c) =>
      Number(c.style.zIndex || 0) > Number(best.style.zIndex || 0) ? c : best,
      stockCards[0]!,
    );
  } else {
    target = boardEl.querySelector<HTMLElement>(
      `.card.placeholder[data-placeholder="stock:0"]`,
    );
  }
  if (!target) return;
  const el = target;
  el.classList.add("hint-flash");
  window.setTimeout(() => el.classList.remove("hint-flash"), 1100);
}

function bindUI(): void {
  btnNew.addEventListener("click", () => void newGame());
  bindUndoButton();
  btnHint.addEventListener("click", handleHint);
  btnSettings.addEventListener("click", openSettings);
  btnRanking.addEventListener("click", showLeaderboard);
  btnJoker.addEventListener("click", handleDrawJoker);

  attachInput(boardEl, stateRef as { current: GameState }, {
    onChange: (move) => {
      if (settings.soundEnabled && move) playMoveSound(move);
      rerender();
    },
    onCancel: () => {
      rerender();
    },
    onDraw: () => {
      if (settings.soundEnabled) playDraw();
    },
    onInvalidTap: () => {
      if (settings.soundEnabled) playInvalid();
      revealDismissedStuckOverlay();
    },
  });

  window.addEventListener("resize", rerender);
  window.addEventListener("pagehide", () => {
    if (!stateRef.current) return;
    // Fold the live session time into elapsedMs so we don't lose play time
    // when the game is resumed later.
    if (timerStartedAt != null) {
      stateRef.current.elapsedMs += Date.now() - timerStartedAt;
      timerStartedAt = Date.now();
    }
    saveProgress(stateRef.current);
    void syncCurrentGameNow(stateRef.current);
  });
}

function playMoveSound(move: Move): void {
  if (
    move.to.kind === "tableau" &&
    (move.from.kind === "waste" || move.from.kind === "tableau")
  ) {
    playDraw();
    return;
  }
  playPlace();
}

function bindUndoButton(): void {
  let holdDelayHandle: number | null = null;
  let repeatHandle: number | null = null;
  let holdActivated = false;

  const clearHold = () => {
    if (holdDelayHandle != null) {
      window.clearTimeout(holdDelayHandle);
      holdDelayHandle = null;
    }
    if (repeatHandle != null) {
      window.clearInterval(repeatHandle);
      repeatHandle = null;
    }
  };

  const stopIfUndoEmpty = () => {
    if (!handleUndo()) clearHold();
  };

  btnUndo.addEventListener("click", (ev) => {
    if (holdActivated) {
      ev.preventDefault();
      holdActivated = false;
      return;
    }
    handleUndo();
  });

  btnUndo.addEventListener("pointerdown", (ev) => {
    if (ev.button !== 0 && ev.pointerType === "mouse") return;
    clearHold();
    holdActivated = false;
    btnUndo.setPointerCapture(ev.pointerId);
    holdDelayHandle = window.setTimeout(() => {
      holdActivated = true;
      stopIfUndoEmpty();
      repeatHandle = window.setInterval(stopIfUndoEmpty, UNDO_REPEAT_MS);
    }, UNDO_HOLD_DELAY_MS);
  });

  const stopHold = () => {
    clearHold();
  };
  btnUndo.addEventListener("pointerup", stopHold);
  btnUndo.addEventListener("pointercancel", stopHold);
  btnUndo.addEventListener("lostpointercapture", stopHold);
  btnUndo.addEventListener("contextmenu", (ev) => ev.preventDefault());
}

function openSettings(): void {
  showSettingsOverlay();
  overlayEl.innerHTML = `
    <div class="settings-view" role="dialog" aria-modal="true" aria-label="Settings">
      <header class="settings-header">
        <button class="settings-back" id="btn-close" type="button" aria-label="Close">‹</button>
        <h2>設定</h2>
        <span></span>
      </header>
      <div class="settings-content">
        <div class="settings-section-title">Game</div>
        <div class="settings-list">
          <label class="settings-row">
            <span class="settings-copy">
              <span class="settings-title">3-card draw</span>
              <span class="settings-subtitle">山札から3枚ずつめくります</span>
            </span>
            <span class="switch">
              <input type="checkbox" id="opt-three" ${settings.drawMode === "three" ? "checked" : ""} />
              <span></span>
            </span>
          </label>
          <label class="settings-row">
            <span class="settings-copy">
              <span class="settings-title">Foundation on left</span>
              <span class="settings-subtitle">組札と山札の左右を切り替えます</span>
            </span>
            <span class="switch">
              <input type="checkbox" id="opt-fonleft" ${settings.foundationOnLeft ? "checked" : ""} />
              <span></span>
            </span>
          </label>
          <label class="settings-row">
            <span class="settings-copy">
              <span class="settings-title">Sound</span>
              <span class="settings-subtitle">効果音を再生します</span>
            </span>
            <span class="switch">
              <input type="checkbox" id="opt-sound" ${settings.soundEnabled ? "checked" : ""} />
              <span></span>
            </span>
          </label>
        </div>

        <div class="settings-section-title">Ranking</div>
        <div class="settings-list">
          <button class="settings-row-button" id="btn-leaderboard" type="button">
            <span class="settings-copy">
              <span class="settings-title">Ranking</span>
              <span class="settings-subtitle">表示名は自動生成されます</span>
            </span>
            <span class="settings-chevron" aria-hidden="true">›</span>
          </button>
          <button class="settings-row-button" id="btn-stats" type="button">
            <span class="settings-copy">
              <span class="settings-title">Statistics</span>
              <span class="settings-subtitle">ドロー枚数別の成績を見る</span>
            </span>
            <span class="settings-chevron" aria-hidden="true">›</span>
          </button>
        </div>

        <div class="settings-summary">
          Lv${levelFromCumulativeXp(stats.cumulativeXp).level} · Won ${stats.gamesWon}/${stats.gamesPlayed}
        </div>
      </div>
    </div>
  `;
  document.getElementById("opt-three")?.addEventListener("change", (e) => {
    settings.drawMode = (e.target as HTMLInputElement).checked ? "three" : "one";
    saveSettings(settings);
  });
  document.getElementById("opt-fonleft")?.addEventListener("change", (e) => {
    settings.foundationOnLeft = (e.target as HTMLInputElement).checked;
    saveSettings(settings);
    rerender();
  });
  document.getElementById("opt-sound")?.addEventListener("change", (e) => {
    settings.soundEnabled = (e.target as HTMLInputElement).checked;
    saveSettings(settings);
    setMuted(!settings.soundEnabled);
  });
  document.getElementById("btn-close")?.addEventListener("click", () => {
    hideOverlay();
  });
  document.getElementById("btn-leaderboard")?.addEventListener("click", () => {
    showLeaderboard();
  });
  document.getElementById("btn-stats")?.addEventListener("click", () => {
    showStatsView(settings.drawMode);
  });
}

async function bootstrap(): Promise<void> {
  injectSuitSprite();
  bindUI();
  const saved = loadProgress();
  if (saved && !saved.finished) {
    ensureJokerState(saved);
    stateRef.current = saved;
    lastFoundationTotal = foundationCount(saved);
    // Already-in-progress games resume their timer immediately.
    timerStartedAt = saved.moves > 0 ? Date.now() : null;
    startTimer();
    rerender();
  } else {
    await newGame();
  }
  // Sync runs in background — never blocks gameplay.
  void bootstrapSync();
}

async function bootstrapSync(): Promise<void> {
  try {
    const session = await ensureAuthenticated();
    if (!session) return;
    void ensureGeneratedDisplayName();
    const merged = await initStatsSync();
    if (merged) {
      stats = merged;
      updateStatusBar();
      updateJokerButton();
    }
    const jokerMerged = await initJokerSync();
    if (jokerMerged) {
      stats = jokerMerged;
      updateStatusBar();
      updateJokerButton();
    }
    if (stateRef.current && !stateRef.current.finished) {
      void syncCurrentGameNow(stateRef.current);
    }
  } catch (e) {
    console.warn("[sync] bootstrap failed:", e);
  }
}

function showLeaderboard(): void {
  showSettingsOverlay();
  overlayEl.innerHTML = `
    <div class="settings-view" role="dialog" aria-modal="true" aria-label="Ranking">
      <header class="settings-header">
        <button class="settings-back" id="btn-lb-close" type="button" aria-label="Close">‹</button>
        <h2>ランキング</h2>
        <span></span>
      </header>
      <div class="settings-content">
        <div class="settings-list leaderboard-list" id="lb-list">
          <p style="opacity: 0.7; text-align: center; padding: 18px;">Loading…</p>
        </div>
      </div>
    </div>
  `;
  document.getElementById("btn-lb-close")?.addEventListener("click", () => {
    hideOverlay();
  });
  void renderLeaderboardInto(document.getElementById("lb-list")!);
}

function showStatsView(mode: DrawMode): void {
  showSettingsOverlay();
  overlayEl.innerHTML = `
    <div class="stats-view" role="dialog" aria-modal="true" aria-label="Statistics">
      <header class="stats-header">
        <button class="stats-back" id="btn-stats-close" type="button" aria-label="Close">‹</button>
        <h2>統計</h2>
        <span></span>
      </header>
      <div class="stats-tabs" role="tablist" aria-label="Draw mode statistics">
        <button class="stats-tab ${mode === "one" ? "active" : ""}" id="btn-stats-one" type="button">ドロー1</button>
        <button class="stats-tab ${mode === "three" ? "active" : ""}" id="btn-stats-three" type="button">ドロー3</button>
      </div>
      <div class="stats-content">
        ${renderStatsBody(mode)}
        <button class="stats-reset" id="btn-stats-reset" type="button">統計をリセット</button>
      </div>
    </div>
  `;
  document.getElementById("btn-stats-close")?.addEventListener("click", () => {
    openSettings();
  });
  document.getElementById("btn-stats-one")?.addEventListener("click", () => {
    showStatsView("one");
  });
  document.getElementById("btn-stats-three")?.addEventListener("click", () => {
    showStatsView("three");
  });
  document.getElementById("btn-stats-reset")?.addEventListener("click", () => {
    if (!window.confirm("統計をリセットしますか？ランキングXPとジョーカーは残ります。")) {
      return;
    }
    stats = resetDetailedStats(stats);
    saveStats(stats);
    scheduleStatsUpload(stats);
    showStatsView(mode);
  });
}

function renderStatsBody(mode: DrawMode): string {
  const s = stats.detailed[mode];
  const played = s.gamesPlayed;
  const winPercent = played > 0 ? Math.round((s.gamesWon / played) * 100) : 0;
  const lossPercent = played > 0 ? Math.round((s.gamesLost / played) * 100) : 0;
  const avgScore = s.gamesWon > 0 ? Math.round(s.totalWinScore / s.gamesWon) : 0;
  const avgWinTime = s.gamesWon > 0 ? Math.round(s.totalWinTimeSec / s.gamesWon) : 0;
  const avgMoves = s.gamesWon > 0 ? Math.round(s.totalWinMoves / s.gamesWon) : 0;

  return `
    <div class="stats-summary-grid">
      ${renderPercentCard("勝利", winPercent, s.gamesWon)}
      ${renderPercentCard("敗北", lossPercent, s.gamesLost)}
    </div>
    <div class="stats-card">
      ${renderStatRow("元に戻さず勝利", formatNumber(s.noUndoWins))}
      ${renderStatRow("ベスト標準スコア", formatNumber(s.bestScore))}
      ${renderStatRow("平均標準スコア", formatNumber(avgScore))}
      ${renderStatRow("ベストベガス累積スコア", "--")}
      ${renderStatRow("ベスト勝利時間", formatDuration(s.bestTimeSec))}
      ${renderStatRow("平均勝利時間", formatDuration(avgWinTime))}
      ${renderStatRow("一番長い勝利時間", formatDuration(s.longestWinTimeSec))}
      ${renderStatRow("ベスト勝利の移動回数", formatNumber(s.bestMoves))}
      ${renderStatRow("勝利の平均移動回数", formatNumber(avgMoves))}
      ${renderStatRow("一番多い勝利の移動回数", formatNumber(s.mostWinMoves))}
      ${renderStatRow("現在の連勝", formatNumber(s.currentWinStreak))}
      ${renderStatRow("最長連勝", formatNumber(s.longestWinStreak), true)}
    </div>
  `;
}

function renderPercentCard(label: string, percent: number, count: number): string {
  const angle = Math.max(0, Math.min(100, percent)) * 3.6;
  return `
    <div class="stats-percent-card">
      <div class="stats-percent-title">${label}</div>
      <div class="stats-ring" style="--ring-angle:${angle}deg"></div>
      <div class="stats-percent-value">${percent}% (${count})</div>
    </div>
  `;
}

function renderStatRow(label: string, value: string, last = false): string {
  return `
    <div class="stats-row ${last ? "last" : ""}">
      <span>${label}</span>
      <strong>${value}</strong>
    </div>
  `;
}

function formatNumber(value: number | null): string {
  if (value == null) return "0";
  return value.toLocaleString();
}

function formatDuration(sec: number | null): string {
  if (sec == null) return "00:00";
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  const s = sec % 60;
  if (h > 0) {
    return `${h}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
  }
  return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}

async function renderLeaderboardInto(target: HTMLElement): Promise<void> {
  const entries = await fetchLeaderboard(50);
  if (entries.length === 0) {
    target.innerHTML = `<p style="opacity: 0.7; text-align: center;">No data yet — be the first!</p>`;
    return;
  }
  target.innerHTML = entries
    .map((e, i) => {
      const flag = e.country ? ` ${escapeHtml(e.country)}` : "";
      return `
        <div style="display:flex; gap:8px; padding:6px 4px; border-bottom:1px solid rgba(255,255,255,0.08);">
          <div style="width:28px; text-align:right; opacity:0.6;">${i + 1}</div>
          <div style="flex:1; overflow:hidden; text-overflow:ellipsis; white-space:nowrap;">
            ${escapeHtml(e.displayName)}${flag}
          </div>
          <div style="width:48px; text-align:right;">Lv${e.level}</div>
          <div style="width:64px; text-align:right; font-variant-numeric: tabular-nums;">
            ${e.cumulativeXp.toLocaleString()}
          </div>
        </div>
      `;
    })
    .join("");
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

void bootstrap();
